import { useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

import Logo from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import {
  openSupportRelinkSession,
  pollSupportRelinkSession,
  requestSupportRelinkCode,
} from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const POLL_INTERVAL_MS = 1500;

function normalizeToken(value = "") {
  return String(value || "").trim();
}

function readPairingCode(payload) {
  return String(payload?.code || payload?.pairingCode || "").trim();
}

function isPreparingError(error) {
  return /prepar/i.test(error?.message || "");
}

export function readSupportRelinkToken(search = "") {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  return normalizeToken(params.get("t") || params.get("token") || params.get("supportToken") || "");
}

export default function SupportRelink({ token }) {
  const { tx } = useI18n();
  const normalizedToken = normalizeToken(token);
  const [session, setSession] = useState(null);
  const [pairingCode, setPairingCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [waitingForConfirmation, setWaitingForConfirmation] = useState(false);
  const [isOpening, setIsOpening] = useState(true);
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const requestInFlightRef = useRef(false);

  const status = String(session?.status || "").toLowerCase();
  const isLinked = status === "linked";
  const isExpired = status === "expired";
  const isFailed = status === "failed";
  const selectedPhone = session?.maskedPhoneNumber || tx("your WhatsApp number");
  const isPreparing = isOpening || (!pairingCode && !errorMessage && !isExpired && !isFailed && !isLinked);
  const canRequestCode = Boolean(
    session?.linkSessionId
      && !pairingCode
      && !isLinked
      && !isExpired
      && !isFailed
      && (status === "awaiting_pairing_code" || status === "awaiting_link"),
  );

  useEffect(() => {
    let cancelled = false;

    async function openSession() {
      if (!normalizedToken) {
        setErrorMessage("This relink link is missing. Message Min Support for a new one.");
        setIsOpening(false);
        return;
      }
      try {
        setErrorMessage("");
        setIsOpening(true);
        const payload = await openSupportRelinkSession(normalizedToken);
        if (!cancelled) {
          setSession(payload);
          const nextCode = readPairingCode(payload);
          if (nextCode) {
            setPairingCode(nextCode);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error?.message || "Could not open this relink link.");
        }
      } finally {
        if (!cancelled) {
          setIsOpening(false);
        }
      }
    }

    void openSession();
    return () => {
      cancelled = true;
    };
  }, [normalizedToken]);

  useEffect(() => {
    if (!canRequestCode || requestInFlightRef.current) {
      return;
    }

    let cancelled = false;
    requestInFlightRef.current = true;
    setIsRequestingCode(true);
    void requestSupportRelinkCode(session.linkSessionId, normalizedToken)
      .then((payload) => {
        if (cancelled) {
          return;
        }
        setSession(payload);
        const nextCode = readPairingCode(payload);
        if (nextCode) {
          setPairingCode(nextCode);
        }
      })
      .catch((error) => {
        if (!cancelled && !isPreparingError(error)) {
          setErrorMessage(error?.message || "Could not get a WhatsApp code.");
        }
      })
      .finally(() => {
        requestInFlightRef.current = false;
        if (!cancelled) {
          setIsRequestingCode(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canRequestCode, normalizedToken, session?.linkSessionId]);

  useEffect(() => {
    if (!session?.linkSessionId || errorMessage || isLinked || isExpired) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      void pollSupportRelinkSession(session.linkSessionId, normalizedToken)
        .then((payload) => {
          setSession(payload);
          if (String(payload?.status || "").toLowerCase() === "linked") {
            setWaitingForConfirmation(false);
          }
        })
        .catch((error) => {
          if (!isPreparingError(error)) {
            setErrorMessage(error?.message || "Could not check relink status.");
          }
        });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [errorMessage, isExpired, isLinked, normalizedToken, session?.linkSessionId]);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background">
      <nav className="flex items-center justify-between px-5 py-4">
        <Logo size="sm" />
      </nav>

      <main className="flex flex-1 items-center justify-center px-5 pb-16">
        <section className="w-full max-w-sm text-center">
          <div className="mb-6">
            <h1 className="font-display text-2xl font-bold">
              {tx(errorMessage
                ? "Relink needs help"
                : isLinked
                  ? "Min is linked again"
                  : isFailed
                    ? "Relink needs help"
                  : isExpired
                    ? "Relink expired"
                    : pairingCode
                      ? "Relink WhatsApp"
                      : "Preparing your code")}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {tx(errorMessage
                ? "Message Min Support and we will send a fresh link."
                : isLinked
                  ? "Send yourself a voice note to test Min."
                  : isFailed
                    ? "Refresh this page or message Min Support."
                  : isExpired
                    ? "Message Min Support and we will send a fresh link."
                    : pairingCode
                      ? "Use this code inside WhatsApp."
                      : "This can take up to 60 seconds.")}
            </p>
          </div>

          <div className="rounded-3xl border border-border bg-card p-6">
            {errorMessage || isExpired || isFailed ? (
              <div className="flex flex-col items-center gap-4">
                <AlertCircle className="h-12 w-12 text-destructive" />
                <p className="text-sm text-muted-foreground">
                  {tx(errorMessage || (isFailed ? "This WhatsApp session could not start." : "This relink link is no longer active."))}
                </p>
              </div>
            ) : isLinked ? (
              <div className="flex flex-col items-center gap-4">
                <CheckCircle2 className="h-12 w-12 text-emerald-600" />
                <p className="text-sm text-muted-foreground">{tx("You can close this page.")}</p>
              </div>
            ) : isPreparing ? (
              <div className="flex min-h-[220px] flex-col items-center justify-center gap-4">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <div>
                  <p className="font-semibold text-foreground">
                    {tx(isRequestingCode ? "Getting your code" : "Connecting to WhatsApp")}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">{selectedPhone}</p>
                </div>
              </div>
            ) : waitingForConfirmation ? (
              <div className="flex min-h-[220px] flex-col items-center justify-center gap-4">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <div>
                  <p className="font-semibold text-foreground">{tx("Waiting for WhatsApp")}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{tx("We will update this page automatically.")}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-5">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <ShieldCheck className="h-7 w-7" />
                </div>
                <div className="w-full rounded-2xl border border-border bg-muted/50 px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{tx("WhatsApp number")}</p>
                  <p className="mt-2 text-base font-semibold text-foreground">{selectedPhone}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{tx("Enter this inside WhatsApp")}</p>
                  <div className="mt-2 rounded-2xl border border-border bg-background px-6 py-4 font-mono text-3xl tracking-[0.35em] text-foreground">
                    {pairingCode}
                  </div>
                </div>
                <div className="grid w-full gap-2 text-left text-sm text-muted-foreground">
                  <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">
                    {tx("1. Tap the WhatsApp notification")}
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">
                    {tx("2. Open")} <span className="font-semibold text-foreground">{tx("Linked Devices")}</span>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">
                    {tx("3. Tap")} <span className="font-semibold text-foreground">{tx("Link with phone number instead")}</span>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">
                    {tx("4. Enter")} <span className="font-semibold text-foreground">{pairingCode}</span>
                  </div>
                </div>
                <Button
                  className="h-12 w-full rounded-xl"
                  onClick={() => setWaitingForConfirmation(true)}
                >
                  {tx("I entered the code")}
                </Button>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
